/**
 * Source: docs/rules/02-creation.md
 *
 * A captain is built one of two ways, both spending the same currency:
 *
 *  - "level": pick an Experience Level, which caps spending and imposes a
 *    minimum Rank. Underspending is legal.
 *  - "cost":  build freely and total the Cost. No cap, no Rank minimum.
 *
 * Either way the captain's final Cost equals the Experience actually spent.
 */

export const EXPERIENCE_LEVEL_IDS = ["green", "rookie", "regular", "veteran", "elite"] as const;

export type ExperienceLevelId = (typeof EXPERIENCE_LEVEL_IDS)[number];

export interface ExperienceLevelDef {
  readonly id: ExperienceLevelId;
  readonly name: string;
  readonly experienceAvailable: number;
  readonly minimumRank: number;
}

export const EXPERIENCE_LEVELS: Record<ExperienceLevelId, ExperienceLevelDef> = {
  green: { id: "green", name: "Green", experienceAvailable: 9, minimumRank: 0 },
  rookie: { id: "rookie", name: "Rookie", experienceAvailable: 19, minimumRank: 0 },
  regular: { id: "regular", name: "Regular", experienceAvailable: 29, minimumRank: 1 },
  veteran: { id: "veteran", name: "Veteran", experienceAvailable: 43, minimumRank: 1 },
  elite: { id: "elite", name: "Elite", experienceAvailable: 59, minimumRank: 2 },
};

/**
 * How a captain's spending is bounded.
 *
 * "level"  -- a fixed creation allowance. Unspent Experience is LOST, since an
 *             Experience Level describes the captain as they enter play.
 * "points" -- a pool the player carries: creation spend plus Experience earned
 *             in play. Unspent Experience remains available.
 * "cost"   -- unbounded. The build's Cost is simply whatever it comes to.
 */
export type BuildMode = "level" | "points" | "cost";

export const BUILD_MODES: readonly { readonly id: BuildMode; readonly label: string }[] = [
  { id: "level", label: "Experience Level" },
  { id: "points", label: "Experience Points" },
  { id: "cost", label: "Cost" },
];
